import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, googleSignIn } from '../services/api';
import toast from 'react-hot-toast';

/**
 * Login - Página de Inicio de Sesión
 * 
 * Permite a los usuarios autenticarse para acceder a funciones protegidas.
 */
const Login = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = await login(formData);
            toast.success('¡Bienvenido de nuevo!');

            // Redirigir según el rol
            localStorage.setItem('userRole', data.user.role);
            localStorage.setItem('userName', `${data.user.firstName} ${data.user.lastName}`);
            localStorage.setItem('userEmail', data.user.email); // Asegurar que guardamos email

            if (data.user.role === 'super_admin' || data.user.email === 'javier@masquefotocopias.es') {
                navigate('/master-admin');
            } else if (data.user.role === 'organizer') {
                navigate('/dashboard');
            } else {
                navigate('/events');
            }
        } catch (error) {
            console.error('Error iniciando sesión:', error);
            const msg = error.response?.data?.message || error.message || 'Credenciales incorrectas';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setLoading(true);
        try {
            const data = await googleSignIn();
            toast.success('¡Bienvenido con Google!');

            // Redirigir según el rol
            if (data.user.role === 'super_admin' || data.user.email === 'javier@masquefotocopias.es') {
                navigate('/master-admin');
            } else if (data.user.role === 'organizer') {
                navigate('/dashboard');
            } else {
                navigate('/events');
            }
        } catch (error) {
            console.error('Error iniciando sesión con Google:', error);
            const msg = error.code === 'auth/popup-closed-by-user'
                ? 'Inicio de sesión cancelado'
                : 'Error al conectar con Google';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0f172a] flex flex-col relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[var(--primary-brand)]/10 rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-[var(--primary-brand)]/5 rounded-full blur-[100px] pointer-events-none"></div>

            {/* Navbar */}
            <nav className="fixed top-0 left-0 w-full z-50 bg-[#0f172a]/95 backdrop-blur-md h-20 md:h-24 transition-all border-b border-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
                    <div className="flex justify-between items-center h-full">
                        <Link to="/" className="flex items-center group">
                            <img 
                                src="/assets/SVG/LogoVisioPadelBlanco.svg?v=2" 
                                alt="VisioPadel" 
                                className="h-14 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                        </Link>

                        <div className="hidden md:flex items-center gap-8 text-[15px] font-light text-slate-200">
                            <Link to="/pricing" className="nav-link-xporty hover:text-white transition-colors">Organiza tu evento</Link>
                            <Link to="/about" className="nav-link-xporty hover:text-white transition-colors">Cómo Funciona</Link>
                            <Link to="/login" className="nav-link-xporty hover:text-white transition-colors active">Entrar</Link>
                            <Link to="/signup" className="nav-link-xporty hover:text-white transition-colors">Registrarse</Link>
                        </div>



                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 text-white"
                        >
                             <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            <div className="flex-grow flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative z-10 pt-32">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-white tracking-tight">
                        Inicia sesión
                    </h2>
                    <p className="mt-2 text-center text-sm text-slate-400">
                        ¿No tienes cuenta?{' '}
                        <Link to="/signup" className="font-medium text-[var(--primary-brand)] hover:text-[#84d93d] transition-colors">
                            Regístrate gratis
                        </Link>
                    </p>
                </div>

                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white/5 backdrop-blur-xl py-8 px-4 shadow-2xl rounded-[2rem] sm:px-10 border border-white/10">
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                                    Correo electrónico
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm transition-all"
                                        placeholder="ejemplo@correo.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                                    Contraseña
                                </label>
                                <div className="mt-1 relative group">
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        autoComplete="current-password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="appearance-none block w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl shadow-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-brand)] focus:border-transparent sm:text-sm pr-12 transition-all"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-500 hover:text-[var(--primary-brand)] focus:outline-none z-10 transition-colors"
                                        title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    >
                                        {showPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.644C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12.013a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <input
                                        id="remember-me"
                                        name="remember-me"
                                        type="checkbox"
                                        className="h-4 w-4 bg-slate-900 border-slate-700 text-[var(--primary-brand)] focus:ring-[var(--primary-brand)] rounded"
                                    />
                                    <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-300">
                                        Recordarme
                                    </label>
                                </div>

                                <div className="text-sm">
                                    <a href="#" className="font-medium text-[var(--primary-brand)] hover:text-[#84d93d] transition-colors">
                                        ¿Olvidaste tu contraseña?
                                    </a>
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-2xl shadow-lg text-sm font-bold uppercase tracking-wide text-white bg-[var(--primary-brand)] transition-all transform hover:-translate-y-1 hover:bg-[#65a30d] active:scale-95 hover:shadow-[#72c02c]/30 ${loading ? 'opacity-70 cursor-wait' : ''}`}
                                >
                                    {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                                </button>
                            </div>
                        </form>

                        <div className="mt-8">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-slate-700" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-[#0f172a] bg-opacity-0 text-slate-500 backdrop-blur-xl">
                                        O continúa con
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-1 gap-3">
                                <button
                                    type="button"
                                    onClick={handleGoogleSignIn}
                                    disabled={loading}
                                    className={`w-full inline-flex justify-center py-3 px-4 border border-slate-700 rounded-xl shadow-sm bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-all transform hover:-translate-y-0.5 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                                        <path
                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            fill="#4285F4"
                                        />
                                        <path
                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            fill="#34A853"
                                        />
                                        <path
                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            fill="#FBBC05"
                                        />
                                        <path
                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            fill="#EA4335"
                                        />
                                    </svg>
                                    <span className="ml-3">Google</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default Login;
